/* ==========================================================================
   Hidecraft — product-info.js
   Variant picker (Section Rendering API), media gallery, lightbox, hover zoom,
   delivery estimate, share button, personalisation counter, sticky add to cart.
   ========================================================================== */

(() => {
  'use strict';

  const { isDesktop, prefersReducedMotion } = window.Hidecraft;
  const strings = window.theme.strings;

  /* ---------- Product info: variant changes ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => {
        if (event.target.closest('variant-picker')) this.onOptionChange(event.target);
      });

      const variant = this.readVariant(this);
      if (variant !== undefined) {
        customElements.whenDefined('product-gallery').then(() => this.gallery?.applyVariant(variant, false));
      }
    }

    get gallery() {
      return this.querySelector('product-gallery');
    }

    readVariant(root) {
      const script = root.querySelector('variant-picker [data-selected-variant]');
      if (!script) return undefined;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    selectedOptionElements() {
      const picker = this.querySelector('variant-picker');
      if (!picker) return [];
      return [...picker.querySelectorAll('input[type="radio"]:checked, select')].map((el) =>
        el.tagName === 'SELECT' ? el.selectedOptions[0] : el
      );
    }

    async onOptionChange(target) {
      const changed = target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      const ids = this.selectedOptionElements()
        .map((el) => el.dataset.optionValueId)
        .filter(Boolean);

      // Instant feedback on the legend while the section loads.
      const legendValue = target.closest('fieldset')?.querySelector('.variant-option__selected');
      if (legendValue) legendValue.textContent = target.value;

      // Combined listings: the value belongs to another product.
      const productUrl = changed?.dataset.productUrl;
      if (productUrl && productUrl !== this.dataset.url) {
        window.location.href = `${productUrl}?option_values=${ids.join(',')}`;
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading-variant');

      const view = this.dataset.view ? `&view=${this.dataset.view}` : '';
      const url = `${this.dataset.url}?option_values=${ids.join(',')}&section_id=${this.sectionId}${view}`;
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        this.applyUpdate(doc, target.id);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn(error);
      } finally {
        this.classList.remove('is-loading-variant');
      }
    }

    applyUpdate(doc, focusId) {
      const fresh = doc.getElementById(`ProductInfo-${this.sectionId}`);
      if (!fresh) return;
      const variant = this.readVariant(fresh);

      this.querySelectorAll('[data-variant-update]').forEach((el) => {
        const replacement = fresh.querySelector(`[data-variant-update="${el.dataset.variantUpdate}"]`);
        if (replacement) el.replaceWith(replacement);
      });
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });

      document.querySelectorAll(`#ProductForm-${this.sectionId} [data-variant-id-input]`).forEach((input) => {
        input.value = variant?.id || '';
        input.disabled = !variant;
      });

      if (variant && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        url.searchParams.delete('option_values');
        window.history.replaceState({}, '', url.toString());
      }

      this.gallery?.applyVariant(variant, true);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Variant picker (keyboard-friendly radios / selects) ---------- */

  class VariantPicker extends HTMLElement {}
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Media gallery ---------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('.product-gallery__viewer');
      this.items = [...this.querySelectorAll('.product-gallery__item')];
      this.thumbItems = [...this.querySelectorAll('.product-gallery__thumb-item')];
      this.dialog = this.querySelector('.product-lightbox');
      this.colorIndex = Number(this.dataset.colorIndex);
      this.layout = [...this.classList].find((c) => c.startsWith('product-gallery--') && !c.includes('mobile'))?.replace('product-gallery--', '');

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumb-media-id]');
        if (thumb) this.showMedia(thumb.dataset.thumbMediaId);

        const open = event.target.closest('[data-lightbox-open]');
        if (open && ['both', 'lightbox'].includes(this.dataset.zoom)) this.openLightbox(Number(open.dataset.lightboxOpen));
      });

      this.addEventListener('slider:change', (event) => {
        if (event.target !== this.viewer) return;
        this.onSlideChange(event.detail.slide);
      });

      if (this.dialog) {
        this.dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
      }

      if (['both', 'hover'].includes(this.dataset.zoom)) this.initHoverZoom();

      if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {} }]);
      }
    }

    onSlideChange(slide) {
      if (!slide) return;
      const id = slide.dataset.mediaId;
      this.thumbItems.forEach((li) => {
        const button = li.querySelector('button');
        if (li.dataset.mediaId === id) {
          button.setAttribute('aria-current', 'true');
          li.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
          button.removeAttribute('aria-current');
        }
      });
      const visible = this.items.filter((item) => !item.hidden);
      const current = this.querySelector('[data-gallery-current]');
      const total = this.querySelector('[data-gallery-total]');
      if (current) current.textContent = visible.indexOf(slide) + 1;
      if (total) total.textContent = visible.length;

      this.querySelectorAll('video').forEach((video) => {
        if (!slide.contains(video)) video.pause();
      });
    }

    showMedia(id, behavior = 'smooth') {
      const item = this.items.find((el) => el.dataset.mediaId === String(id));
      if (!item || item.hidden || !this.viewer?.slides) return;

      // Grid/stacked layouts show everything on desktop: bring the media to the top.
      if (isDesktop() && ['grid', 'stacked'].includes(this.layout)) {
        if (this.viewer.track.firstElementChild !== item) {
          this.viewer.track.prepend(item);
          this.viewer.refresh();
        }
        return;
      }
      const index = this.viewer.slides.indexOf(item);
      if (index > -1) this.viewer.goTo(index, prefersReducedMotion() ? 'auto' : behavior);
    }

    applyVariant(variant, animate) {
      if (!variant) return;
      if (this.dataset.filter === 'true' && this.colorIndex >= 0) {
        const group = variant.options[this.colorIndex];
        const hasGroup = this.items.some((item) => item.dataset.mediaGroup === group);
        const hide = (el) => hasGroup && el.dataset.mediaGroup !== '' && el.dataset.mediaGroup !== group;
        this.items.forEach((item) => (item.hidden = hide(item)));
        this.thumbItems.forEach((li) => {
          const item = this.items.find((el) => el.dataset.mediaId === li.dataset.mediaId);
          li.hidden = item ? item.hidden : false;
        });
        this.viewer?.refresh?.();
      }
      if (variant.featured_media) {
        this.showMedia(variant.featured_media.id, animate ? 'smooth' : 'auto');
      } else {
        this.viewer?.goTo?.(0, 'auto');
      }
    }

    openLightbox(index) {
      if (!this.dialog) return;
      this.dialog.showModal();
      const slider = this.dialog.querySelector('slider-component');
      requestAnimationFrame(() => {
        slider?.update?.();
        slider?.goTo?.(index, 'auto');
      });
    }

    initHoverZoom() {
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.items
        .filter((item) => item.dataset.mediaType === 'image')
        .forEach((item) => {
          const media = item.querySelector('.product-gallery__media');
          media.addEventListener('mousemove', (event) => {
            if (!canHover.matches) return;
            const rect = media.getBoundingClientRect();
            media.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
            media.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
            media.classList.add('is-zoomed');
          });
          media.addEventListener('mouseleave', () => media.classList.remove('is-zoomed'));
        });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Delivery estimate ---------- */

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(min, Number(this.dataset.max) || min);
      const skip = { none: [], sunday: [0], friday: [5], weekend: [0, 6] }[this.dataset.skip] || [];

      const addDays = (days) => {
        const date = new Date();
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          if (!skip.includes(date.getDay())) added += 1;
        }
        return date;
      };

      let format;
      try {
        format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      } catch (e) {
        format = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      }
      const text = (strings.deliveryTemplate || '')
        .replace('[start]', format.format(addDays(min)))
        .replace('[end]', format.format(addDays(max)));
      const target = this.querySelector('[data-delivery-text]');
      if (target) target.innerHTML = text;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Share button (native share, falls back to copy link) ---------- */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', async () => {
        const { url, title } = this.dataset;
        if (navigator.share) {
          try {
            await navigator.share({ url, title });
            return;
          } catch (e) {
            if (e.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          const label = this.querySelector('[data-share-label]');
          if (label) {
            const original = label.textContent;
            label.textContent = strings.copied;
            setTimeout(() => (label.textContent = original), 2000);
          }
        } catch (e) {
          window.prompt('', url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Personalisation character counter ---------- */

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-char-counter]');
    if (!input) return;
    const counter = document.getElementById(input.getAttribute('aria-describedby'));
    const left = Number(input.maxLength) - input.value.length;
    if (counter) counter.textContent = (strings.charactersLeft || '').replace('[count]', left);
  });

  /* ---------- Sticky add to cart ---------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.observe();
      this.closest('product-info')?.addEventListener('variant:change', () => this.observe());
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }

    observe() {
      this.observer?.disconnect();
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      this.observer = new IntersectionObserver(([entry]) => {
        this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(target);
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      document.body.classList.toggle('has-sticky-atc', show);
    }
  }
  customElements.define('sticky-atc', StickyAtc);
})();
